import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FlightRoomUserWithName,
  useChannels,
  useChatChatroomSocketSendEvents,
  useEnrichedFlightRoomUsers,
  useFlightRooms,
} from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { FlightRoomUserStatus } from '@connect-x-app/shared-domain-models';
import { COLORS } from '@connect-x-app/shared-ui';
import { CHANNEL_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Button, Checkbox, Modal, ModalOpener, toastError } from '@connect-x-app/web-ui';

import './userRequestAccessModal.scss';

type SelectableUser = FlightRoomUserWithName & {
  selected: boolean;
};

export const UserRequestAccessModal = () => {
  const { selectedFlightRoom: room } = useFlightRooms();
  const { roomChannels } = useChannels();
  const { t } = useTranslation();
  const { sendDeclineAccess, sendGrantAccess } = useChatChatroomSocketSendEvents();
  const [isApproveLoading, setIsApproveLoading] = useState(false);
  const [isDenyLoading, setIsDenyLoading] = useState(false);

  // Memoize requests to prevent unnecessary re-renders
  const pendingRequests = useMemo(() => {
    return roomChannels?.mainChannel.users?.filter(({ status }) => status === FlightRoomUserStatus.PENDING) ?? [];
  }, [roomChannels?.mainChannel.users]);

  //close popup when no requests are available
  useEffect(() => {
    if (pendingRequests.length === 0) {
      ModalOpener.instance.close();
    }
  }, [pendingRequests]);

  const enrichedUsers = useEnrichedFlightRoomUsers(pendingRequests);

  const [selectedUsers, setSelectedUsers] = useState<SelectableUser[]>([]);

  // Effect to set selected users when requests or users change
  useEffect(() => {
    setSelectedUsers(enrichedUsers.map(user => ({ ...user, selected: false })));
  }, [enrichedUsers, pendingRequests]);

  const selected = selectedUsers.filter(({ selected }: { selected: boolean }) => selected);

  const areSomeSelected = selected.length > 0;

  const areAllSelected = selected.length === selectedUsers.length;

  const toggleAll = () => {
    setSelectedUsers(prevSelectedUsers =>
      prevSelectedUsers.map(user => ({
        ...user,
        selected: !areAllSelected,
      })),
    );
  };

  const toggleSelect = (needle: SelectableUser) => {
    setSelectedUsers(prevSelectedUsers => {
      const index = prevSelectedUsers.findIndex(haystack => haystack.externalId === needle.externalId);
      return [
        ...prevSelectedUsers.slice(0, index),
        { ...needle, selected: !needle.selected },
        ...prevSelectedUsers.slice(index + 1),
      ];
    });
  };

  const handleAccessPermission = async (granted: boolean) => {
    if (selected.length === 0 || !room) {
      return;
    }

    try {
      let hasError = false;
      for (const { id: selectedUserId } of selected) {
        const result = granted
          ? await sendGrantAccess({ chatroomId: room.id, participantId: selectedUserId })
          : await sendDeclineAccess({ chatroomId: room.id, participantId: selectedUserId });
        if (!result?.ok) {
          hasError = true;
        }
      }
      if (hasError) {
        toastError(t('ERRORS.ACCESS_REQUEST_FAILED'));
      }
    } catch {
      toastError(t('TOAST_ERROR'));
    } finally {
      setIsApproveLoading(false);
      setIsDenyLoading(false);
      ModalOpener.instance.close();
    }
  };

  const handleApproveClicked = () => {
    setIsApproveLoading(true);
    handleAccessPermission(true);
  };

  const handleDenyClicked = () => {
    setIsDenyLoading(true);
    handleAccessPermission(false);
  };

  return (
    <Modal>
      <Modal.Header>
        {t(
          selectedUsers.length > 1
            ? 'FLIGHTROOMS.USER_REQUESTS_ACCESS.MODAL.TITLE'
            : 'FLIGHTROOMS.USER_REQUEST_ACCESS.MODAL.TITLE',
          {
            amount: selectedUsers.length,
            flightroomName: room?.flightNumber,
          },
        )}
      </Modal.Header>
      <div className="flightroom-request-modal-center">
        <button
          className="flightroom-request-modal-select-all"
          data-test={CHANNEL_TEST_IDS.accessRequestModal.selectAll}
          onClick={toggleAll}
          type="button"
        >
          <Text color={COLORS.romeo.$07} size="LG" weight="bold">
            {t(
              areAllSelected ? 'FLIGHTROOMS.USER_REQUEST_ACCESS.MODAL.DESELECT' : 'FLIGHTROOMS.USER_REQUEST_ACCESS.MODAL.SELECT',
            )}
          </Text>
        </button>
        {selectedUsers.map(user => (
          <div className="flightroom-request-modal-content" key={user.externalId}>
            <Checkbox
              checked={user.selected}
              className="flightroom-request-checkbox"
              label={`${user.division} ${user.name}`}
              labelClassName="flightroom-request-checkbox-label"
              name={t('APPROVE_ACCESS.ALERT.TITLE', { name: user.name })}
              onChange={() => toggleSelect(user)}
              testId={`modal-checkbox-${user.name}`}
            />
          </div>
        ))}
      </div>
      <Modal.Buttons>
        <Button
          disabled={!areSomeSelected || isApproveLoading || isDenyLoading}
          isOutline
          loading={isDenyLoading}
          onClick={handleDenyClicked}
          testId={CHANNEL_TEST_IDS.accessRequestModal.deny}
          theme="negative"
        >
          {t('APPROVE_ACCESS.SHEET.DENY')}
        </Button>
        <Button
          disabled={!areSomeSelected || isApproveLoading || isDenyLoading}
          loading={isApproveLoading}
          onClick={handleApproveClicked}
          testId={CHANNEL_TEST_IDS.accessRequestModal.approve}
          theme="primary"
        >
          {t('APPROVE_ACCESS.SHEET.APPROVE')}
        </Button>
      </Modal.Buttons>
    </Modal>
  );
};
